import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { filterOptions } from "./lead-filter/lead-filter.constant";
import _ from "lodash";
import "./lead-captured-form-lead-list.scss";
import { getLeads } from "../../../api/lead";
import { useLogin } from "../../../common/hooks/useUser";
import {
  AlertComponent,
  BreadcrumbComponent,
  MUITable,
  PaginationComponent,
} from "../../../common/components";
import {
  breadcrumbLink,
  breadcrumbLinkLeadList,
} from "../lead-capture-list.constant";
import LeadFilter from "./lead-filter/lead-filter";
import LeadsStatus from "../leads-status";
import LeadFilterOptions from "./lead-filter/lead-filter-options";
import { routes } from "../../../constants/routes";
import LeadView from "../../leads/lead-view/lead-view";
import CreateLeadCaptureForm from "../create-lead-capture-form/create-lead-capture-form";
import { getLeadCaptureForms } from "../../../api/lead-capture";
import { useLocation, useNavigate } from "react-router-dom";
import { isValidPhoneNumber } from "../../../utils/utils";

interface LeadCapturedFormLeadListProps {
  selectedLeadCaptureFormId?: string | number;
  selectedLeadCaptureFormName?: string;
  loading?: any;
  setLoading?: (loading: boolean) => void;
  viewOpenForm?: any;
  setViewOpenForm?: any;
  // crumbMappingOption: any;
  // setCrumbMappingOption: React.Dispatch<React.SetStateAction<any>>;
}
interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}
const LeadCapturedFormLeadList: React.FC<LeadCapturedFormLeadListProps> = (
  props: any,
) => {
  const { loading, viewOpenForm, setViewOpenForm } = props;
  // Use setLoading from props if provided, otherwise define a local one
  const [internalLoading, internalSetLoading] = useState(false);
  const setLoading = props.setLoading || internalSetLoading;
  const effectiveLoading =
    typeof loading !== "undefined" ? loading : internalLoading;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedLeadCaptureFormId: selectedLeadCaptureFormIdFromState,
    selectedLeadCaptureFormName: selectedLeadCaptureFormNameFromState,
    fromNotificationsPopup
  } = location.state || {};
  const selectedLeadCaptureFormId =
    props.selectedLeadCaptureFormId || selectedLeadCaptureFormIdFromState;
  const selectedLeadCaptureFormName =
    props.selectedLeadCaptureFormName || selectedLeadCaptureFormNameFromState;
  const [crumbMappingOption, setCrumbMappingOption] = useState<any>([
    {
      label: "Lead Capture",
      link: routes.LEADCAPTUREFORM,
      handleClick: () => {
        setViewOpenForm(false);
        setCrumbMappingOption((prev: any) => prev.slice(0, 0));
        if (fromNotificationsPopup) {
          navigate(routes.LEADCAPTUREFORM);
        }
      },
    },
    {
      label: "Form List",
      link: routes.LEADCAPTUREFORM,
      handleClick: () => {
        setViewOpenForm(false);
        setCrumbMappingOption((prev: any) => prev.slice(0, 1));
        if (fromNotificationsPopup) {
          navigate(routes.LEADCAPTUREFORM);
        }
      },
    },
    {
      label: selectedLeadCaptureFormName,
      link: "",
    },
    {
      label: "Leads",
      link: "",
    },
  ]);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedLeadsFilter, setSelectedLeadsFilters] = useState<number[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0); // Set an initial value for testing
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedLeadId, setSelectedLeadId] = useState<any>(null);
  const [selectedLeadName, setSelectedLeadName] = useState<string>("");
  const [isShowCreateLeadSection, setIsShowCreateLeadSection] =
    useState<boolean>(false);
  const [createLeadCaptureInitialValues, setCreateLeadCaptureInitialValues] =
    useState<any>(null);
  const [createLeadInitialValues, setCreateLeadInitialValues] =
    useState<any>(null);
  const isSearchTriggered = useRef(false);
  const { tenantDetails } = useLogin();
  const filteredLeadFilterOptions = useMemo(() => {
    if (tenantDetails?.tenantType === "Tenant") {
      return filterOptions.filter((opt) => opt.labels !== "Assigned Partner");
    }
    return filterOptions;
  }, [tenantDetails, filterOptions]);
  const [selectedLeadsFilterOptions, setSelectedLeadsFilterOptions] = useState<
    any[]
  >(filteredLeadFilterOptions);
  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1);
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    [],
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };
  const userDetails = useLogin();
  const leadOwner =
    userDetails?.userDetails?.firstName +
    " " +
    userDetails?.userDetails?.lastName;

  const fetchLeads = async (
    page = 1,
    search: string = "",
    filters: any = {},
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
        leadCaptureFormId: selectedLeadCaptureFormId,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      if (filters?.annualRevenue?.min) {
        params.leadValueMin = filters.annualRevenue.min;
      }
      if (filters?.annualRevenue?.max) {
        params.leadValueMax = filters.annualRevenue.max;
      }
      if (filters?.email) {
        params.email = filters.email;
      }
      if (filters?.city) {
        params.city = filters.city;
      }
      if (filters?.country) {
        params.country = filters.country;
      }
      if (filters?.fax) {
        params.fax = filters.fax;
      }
      if (filters?.firstName) {
        params.firstName = filters.firstName;
      }
      if (filters?.lastName) {
        params.lastName = filters.lastName;
      }
      if (filters?.company) {
        params.company = filters.company;
      }
      if (filters?.industry) {
        params.industry = filters.industry;
      }
      if (filters?.confidenceScore) {
        params.confidenceScore = filters.confidenceScore;
      }
      if (filters?.leadStatus) {
        params.leadStatus = filters.leadStatus;
      }
      if (filters?.createdTimeStart && filters.createdTimeEnd) {
        // if (filters.createdTimeStart.isSame(filters.createdTimeEnd)) {
        params.createdTimeStart = filters.createdTimeStart.startOf("day");
        params.createdTimeEnd = filters.createdTimeEnd.endOf("day");
        // } else {
        //   params.createdTimeStart = filters.createdTimeStart;
        //   params.createdTimeEnd = filters.createdTimeEnd
        // }
      }
      if (filters?.lastActivityTimeStart && filters.lastActivityTimeEnd) {
        // if (filters.lastActivityTimeStart.isSame(filters.lastActivityTimeEnd)) {
        params.lastActivityStart = filters.lastActivityTimeStart.startOf("day");
        params.lastActivityEnd = filters.lastActivityTimeEnd.endOf("day");
        // } else {
        //   params.lastActivityStart = filters.lastActivityTimeStart;
        //   params.lastActivityEnd = filters.lastActivityTimeEnd
        // }
      }
      if (filters?.createdByUserIds?.length > 0) {
        params.createdBy = filters.createdByUserIds.map(Number);
      }
      if (filters?.leadOwnerIds?.length > 0) {
        params.leadOwner = filters.leadOwnerIds.map(Number);
      }
      if (filters?.assignedPartnerIds?.length > 0) {
        params.assignedPartner = filters.assignedPartnerIds.map(Number);
      }
      const response = await getLeads(params, "Lead Capture");
      setData(response.data.data.leads);
      setTotalRecords(response.data.data.totalRecords);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns",
      );
      setAlertType("error");
      setAlertOpen(true);
      setError("Failed to fetch leads list");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!viewOpen) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchLeads(pageToUse, searchText, activeFilters);
      isSearchTriggered.current = false;
    }
  }, [currentPage, searchText, isOpen, sortOption, activeFilters]);

  useEffect(() => {
    if (!viewOpen) {
      setCurrentPage(1);
      fetchLeads(1, searchText, activeFilters);
    }
  }, [viewOpen]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };
  const headers = [
    { key: "leadName", label: "Lead Name", isSortable: true },
    { key: "email", label: "Email", isSortable: true },
    { key: "phone", label: "Phone Number", isSortable: true },
    { key: "leadSource", label: "Lead Source", isSortable: true },
    { key: "leadOwner", label: "Lead Owner", isSortable: true },
    // { key: "leadCreatedBy", label: "Created By", isSortable: true },
    { key: "leadStatus", label: "Lead Status", isSortable: true },
  ];
  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      leadName: "first_name",
      email: "primary_email_address",
      phone: "primary_phone_number",
      leadSource: "lead_source",
      leadOwner: "lead_owner",
      leadStatus: "lead_status",
      // leadCreatedBy: "created_by",
    };

    return mapping[columnName] || columnName;
  };
  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };
  const fetchFullName = (item: any) => {
    const firstName = item?.first_name?.trim();
    const lastName = item?.last_name?.trim();
    const fullName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName
          ? firstName
          : lastName
            ? lastName
            : "-";
    return fullName;
  };
  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        const firstName = item.first_name?.trim();
        const lastName = item.last_name?.trim();
        const middleName = item?.middle_name ? item?.middle_name?.trim() : "";
        const fullName =
          firstName && lastName
            ? `${firstName ? `${firstName} ` : ""}${middleName ? `${middleName} ` : ""}${lastName}`
            : firstName || middleName || lastName || "-";

        const leadName = fullName;
        const leadOwnerName = fetchFullName(item?.owner);
        // const createdBy = fetchFullName(item?.createdBy)
        return {
          id: item.id,
          leadName: leadName,
          email: item.primary_email_address || "-",
          phone: isValidPhoneNumber(item.primary_phone_number)
            ? item.primary_phone_number
            : "-",
          leadSource: item.lead_source || "-",
          leadOwner: leadOwnerName,
          leadStatus: item.lead_status || "-",
          // leadCreatedBy: createdBy
        };
      });
    }
    return [];
  }, [data, leadOwner]);
  const handleViewClick = async (leadId: number, leadName: string) => {
    setSelectedLeadId(leadId);
    setSelectedLeadName(leadName);
    setCrumbMappingOption([
      {
        label: "Lead Capture",
        link: routes.LEADCAPTUREFORM,
        handleClick: () => {
          setViewOpenForm(false);
          setCrumbMappingOption((prev: any) => prev.slice(0, 1));
        },
      },
      {
        label: "Form List",
        link: routes.LEADCAPTUREFORM,
        handleClick: () => {
          setViewOpenForm(false);
          setCrumbMappingOption((prev: any) => prev.slice(0, 2));
        },
      },
      {
        label: selectedLeadCaptureFormName,
        link: "",
      },
      {
        label: "Leads",
        link: "",
        handleClick: () => {
          setViewOpen(false);
          setSelectedLeadId(null);
          setSelectedLeadName("");
          setCrumbMappingOption((prev: any) => prev.slice(0, 4));
        },
      },
      { label: leadName, link: "" },
    ]);

    setViewOpen(true);
  };
  const handleLeadNameUpdate = (newName: string) => {
    setSelectedLeadName(newName);
    setCrumbMappingOption((prev: any) => {
      const updated = [...prev];
      updated[3] = { ...updated[3], label: newName };
      return updated;
    });
  };

  const createUpdateLeadList = () => {
    // fetchLeads();
    // setSearchText("");
    // setActiveFilters({});
    // setSelectedLeadsMap({});
    // setCurrentPage(1);
    // setSelectedLeads([]);
    // setSelectedLeadsFilters([]);
    // setSelectedLeadsFilterOptions(filterOptions);
    // setSelectedAllLeads(null);
    // setSelectedCreateLeads(null);
    // setSelectedActions(null);
    // setSortOption({ sortBy: null, sortDirection: null });
  };
  const fetchLeadCaptureForms = async (
    page = 1,
    search: string = "",
    filters: any = {},
  ) => {
    setLoading(true);
    setCrumbMappingOption(breadcrumbLink);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (search) {
        params.searchText = search;
      }
      if (filters.formName) {
        params.form_title = filters.formName;
      }
      const response = await getLeadCaptureForms(params);
      setData(response.data.data.data);
      setTotalRecords(response.data.data.total);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns",
      );
      setAlertType("error");
      setAlertOpen(true);
      setError("Failed to fetch leads list");
      setLoading(false);
    }
  };
  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const runFilterActive =
      activeFilters.lastActivityTimeStart || activeFilters.lastActivityTimeEnd;
    const minFilter =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;
    delete otherFilters.lastActivityTimeStart;
    delete otherFilters.lastActivityTimeEnd;

    const otherActiveFilters = Object.values(otherFilters).filter((val) => {
      if (val === undefined || val === "" || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") {
        return Object.values(val).some(
          (v) => v !== "" && v !== undefined && v !== null,
        );
      }
      return true;
    }).length;
    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);
  const noResultText = useMemo(() => {
    if (effectiveLoading) {
      return "Loading Leads...";
    }
    if (searchText) {
      return "No leads found matching your search criteria";
    }
    return "No leads have been captured from this form yet";
  }, [effectiveLoading, searchText]);

  return (
    <>
      {isShowCreateLeadSection && (
        <CreateLeadCaptureForm
          setCreateLeadCaptureInitialValues={setCreateLeadCaptureInitialValues}
          createLeadCaptureInitialValues={createLeadCaptureInitialValues}
          setIsShowCreateLeadSection={setIsShowCreateLeadSection}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setAlertOpen={setAlertOpen}
          setLoading={setLoading}
          fetchLeadCaptureForms={fetchLeadCaptureForms}
          setViewOpenForm={setViewOpenForm}
        />
      )}

      <div
        className="lead-captured-form-leadlist-wrapper"
        style={{ display: isShowCreateLeadSection ? "none" : "block" }}
      >
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent
            breadcrumbLink={crumbMappingOption}
            showBackArrow={true}
            handleBackArrowClick={() => {
              setViewOpenForm(false);
              if (fromNotificationsPopup) {
                navigate(routes.LEADCAPTUREFORM);
              }
            }}
          />
        </div>

        <div style={{ display: !viewOpen ? "block" : "none" }}>
          <LeadFilter
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            selectedLeadsFilter={appliedFilterCount}
            onSearch={handleSearch}
            searchRef={searchRef}
            isResetSearch={isResetSearch}
            resetSearch={resetSearch}
            handleClickCreateNewForm={() => {
              setIsShowCreateLeadSection(true);
              setCreateLeadInitialValues(null);
            }}
            fetchLeads={() => {
              setIsResetSearch(!isResetSearch);
              setSearchText("");
              fetchLeads(currentPage);
            }}
          />

          <div className="lead-captured-form-lead-list-table-filter">
            <div
              className="lead-captured-form-lead-list-table"
              style={isOpenFilter ? { width: "80%" } : {}}
            >
              <MUITable
                headers={headers}
                data={formattedData.map((item) => ({
                  key: item.id,
                  leadName: item.leadName,
                  email: item.email,
                  phone: item.phone,
                  leadSource: item.leadSource,
                  leadOwner: item.leadOwner,
                  //leadCreatedBy: item.leadCreatedBy,
                  leadStatus: <LeadsStatus status={item.leadStatus} />,
                }))}
                onSort={handleSort}
                noResultsTxt={noResultText}
                onRowClick={(rowData: any) =>
                  handleViewClick(rowData.key, rowData.leadName)
                }
              />
            </div>

            <div
              style={{ display: isOpenFilter ? "block" : "none" }}
              className="lead-captured-form-lead-list-filter"
            >
              <LeadFilterOptions
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
                selectedLeadsFilter={selectedLeadsFilter}
                setSelectedLeadsFilters={setSelectedLeadsFilters}
                selectedLeadsFilterOptions={selectedLeadsFilterOptions}
                setAlertMessage={setAlertMessage}
                setAlertType={setAlertType}
                setAlertOpen={setAlertOpen}
                setSelectedLeadsFilterOptions={setSelectedLeadsFilterOptions}
                onApplyFilters={(filters: any) => {
                  setCurrentPage(1);
                  setActiveFilters(filters);
                }}
                activeFilters={activeFilters}
              />
            </div>
            {totalRecords && totalRecords > 0 ? (
              <div className="leads-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>

        {viewOpen && (
          <LeadView
            setLoading={setLoading}
            selectedLeadId={selectedLeadId}
            onLeadNameUpdate={handleLeadNameUpdate}
          />
        )}
      </div>

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default LeadCapturedFormLeadList;
